/**
 * CulinaryDemoContent - EXACT replica of phygital.systems/culinary landing page
 * Used as demo tab content for Culinary pilot invites
 * All text and images are editable by super admin
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useAdmin, EditableText, EditableImage } from "@/components/AdminContentEditor";
import {
  ArrowRight,
  Check,
  ChefHat,
  Cake,
  Users,
  Calendar,
  UtensilsCrossed,
  Image as ImageIcon,
  AlertCircle
} from "lucide-react";

export default function CulinaryDemoContent({ onAccept, portfolioImages = [] }) {
  const { editMode } = useAdmin();

  // Culinary services from exact PDF
  const culinaryServices = [
    { icon: ChefHat, name: "Private Chef", desc: "In-home dining experiences" },
    { icon: Cake, name: "Custom Cakes", desc: "Birthday, wedding, celebration cakes" },
    { icon: Users, name: "Event Catering", desc: "Corporate & private events" },
    { icon: Calendar, name: "Meal Prep", desc: "Weekly meal prep services" }
  ];

  // Features from exact PDF
  const features = [
    {
      title: "Client taste profiles",
      desc: "Dietary restrictions, allergies, cuisine preferences, spice tolerance - all captured upfront"
    },
    {
      title: "Menu planning",
      desc: "Share custom menus, get approvals, and track dietary needs for every event"
    },
    {
      title: "Service customization",
      desc: "In-home dinners, event catering, cake orders - manage it all in one place"
    },
    {
      title: "Guest management",
      desc: "Track preferences for each guest at multi-person events"
    }
  ];

  // Dietary options from exact PDF
  const dietaryOptions = ["Vegetarian", "Vegan", "Gluten-Free", "Keto", "Halal", "Kosher"];
  
  // Allergy options from exact PDF
  const allergyOptions = ["Nuts", "Shellfish", "Dairy", "Eggs", "Soy", "Wheat"];
  
  // Cuisine options from exact PDF
  const cuisineOptions = ["Italian", "French", "Asian Fusion", "Mediterranean", "Southern", "Mexican"];
  
  // Preference options from exact PDF
  const preferenceOptions = ["Spicy", "Mild", "No Cilantro", "Extra Garlic", "Low Sodium", "Organic"];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Exact from PDF */}
      <section className="py-16 px-4 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-6xl mx-auto text-center">
          <Badge className="mb-4 bg-orange-100 text-orange-700">
            Culinary
          </Badge>
          <p className="text-sm text-gray-600 mb-4">For Private Chefs & Bakers</p>
          
          <EditableText
            sectionId="culinary-demo-hero"
            field="title"
            defaultValue="Every guest's preferences, perfectly captured"
            className="text-4xl sm:text-5xl font-bold mb-6"
            as="h1"
          />
          
          <EditableText
            sectionId="culinary-demo-hero"
            field="subtitle"
            defaultValue="Stop asking the same questions. Know dietary restrictions, allergies, and taste preferences before you plan the menu."
            className="text-gray-600 max-w-2xl mx-auto text-lg mb-8"
            as="p"
          />

          <Button onClick={onAccept} className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full px-8 h-12">
            Accept Invite
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Built for every culinary service - Exact from PDF */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Built for every culinary service</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {culinaryServices.map((service, i) => (
              <Card key={i} className="text-center hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center mx-auto mb-4">
                    <service.icon className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="font-semibold mb-1">{service.name}</h3>
                  <p className="text-sm text-gray-600">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stop guessing. Start knowing. - Exact from PDF */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-orange-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-2">Stop guessing. Start knowing.</h2>
            <p className="text-gray-600">Every dietary restriction and preference captured before you start cooking</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What you'll know about every client - Exact from PDF */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-2">What you'll know about every client</h2>
            <p className="text-gray-600">Detailed preferences collected automatically</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Dietary */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4 text-orange-700">Dietary:</h3>
                <div className="flex flex-wrap gap-2">
                  {dietaryOptions.map(opt => (
                    <Badge key={opt} variant="outline" className="cursor-pointer hover:bg-orange-50">{opt}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Allergies */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4 text-red-700">Allergies:</h3>
                <div className="flex flex-wrap gap-2">
                  {allergyOptions.map(opt => (
                    <Badge key={opt} variant="outline" className="cursor-pointer hover:bg-red-50">{opt}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Cuisine */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4 text-amber-700">Cuisine:</h3>
                <div className="flex flex-wrap gap-2">
                  {cuisineOptions.map(opt => (
                    <Badge key={opt} variant="outline" className="cursor-pointer hover:bg-amber-50">{opt}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Preferences */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4 text-green-700">Preferences:</h3>
                <div className="flex flex-wrap gap-2">
                  {preferenceOptions.map(opt => (
                    <Badge key={opt} variant="outline" className="cursor-pointer hover:bg-green-50">{opt}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* For Bakers - Custom cake orders - Exact from PDF */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-orange-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-pink-100 text-pink-700">For Bakers</Badge>
              <h2 className="text-3xl font-bold mb-4">Custom cake orders, simplified</h2>
              <p className="text-gray-600 mb-6">
                Know exactly what your client wants before they place the order. 
                Flavor preferences, allergies, design inspiration - all in one place.
              </p>

              <ul className="space-y-3">
                {[
                  "Flavor & filling preferences",
                  "Design inspiration boards",
                  "Allergy & dietary needs",
                  "Delivery/pickup preferences",
                  "Past order history"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Cake Order Example Card - Exact from PDF */}
            <Card className="border-2 border-pink-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">New Order: Sarah's Birthday Cake</h3>
                  <Badge variant="outline">Due: March 15, 2026</Badge>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Flavor:</span>
                    <span className="font-medium">Red Velvet</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Filling:</span>
                    <span className="font-medium">Cream Cheese</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dietary:</span>
                    <Badge className="bg-amber-100 text-amber-700">Nut-Free</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Style:</span>
                    <span className="font-medium">Floral, Elegant</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-gray-600 mb-2">Inspo photos attached (3)</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[1, 2, 3].map(num => (
                      <EditableImage
                        key={num}
                        sectionId="culinary-demo-cake"
                        field={`inspo_${num}`}
                        defaultImage={null}
                        className="aspect-square rounded-lg overflow-hidden"
                        placeholderClassName="bg-gradient-to-br from-orange-100 to-red-100"
                        placeholderIcon={<ImageIcon className="w-4 h-4 text-orange-300" />}
                      />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section - Exact from PDF */}
      <section className="py-20 px-4 bg-gradient-to-r from-orange-500 to-red-500 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to elevate your culinary business?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Join our pilot program and help shape the future of personalized culinary experiences.
          </p>
          <Button
            onClick={onAccept}
            size="lg"
            className="bg-white text-orange-600 hover:bg-gray-100 h-14 px-10 rounded-full text-lg font-semibold"
          >
            Accept Invite
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
}
